import type { AuthChangeEvent } from "@supabase/supabase-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";
import {
  fetchOwnerContext,
  OWNER_CONTEXT_GC_MS,
  OWNER_CONTEXT_QUERY_KEY,
  OWNER_CONTEXT_STALE_MS,
  OwnerContextStateContext,
  ownerContextQueryKey,
  resetNonReadyOwnerContext,
  shouldKeepQueryOnUserChange,
  type OwnerContextState,
} from "../hooks/useOwnerContext";
import { supabase } from "../integrations/supabase/client";
import { readPersistedSessionUserId } from "../integrations/supabase/sessionSnapshot";
import { clearActorRoleHint } from "../utils/actorRoleHint";
import { useInstructorClient } from "./InstructorClientContext";

/**
 * Resolves the owner/actor context once per signed-in user and shares it with the whole app
 * (shell, nav, route gates, pages) through `useOwnerContext` / `useActorRole`.
 *
 * - The account's own context (`base`) is always observed here, so it is never garbage-collected
 *   while the app is open — opening a client and coming back to the roster reuses it instead of
 *   reloading it (which used to drop the nav back to the personal items for a moment).
 * - The open client's context is a layer derived from `base` (reusing its verified actor id and
 *   role) and keyed by the URL's client id; it only runs for a resolved instructor.
 * - Every entry is keyed by the session user id, and any auth change to a different user drops
 *   every user-specific query, so neither a stale "unauthenticated" result nor a previous
 *   account's role or data can survive into the next session.
 */
export const OwnerContextProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient();
  const { activeClient } = useInstructorClient();
  const clientUserId = activeClient?.clientUserId ?? null;

  // First paint uses the persisted session synchronously, so a returning user's cache entry and
  // role hint are picked up without waiting for any async auth call.
  const [sessionUserId, setSessionUserId] = useState<string | null>(() =>
    readPersistedSessionUserId()
  );
  const userIdRef = useRef(sessionUserId);

  useEffect(() => {
    let isUnmounted = false;

    const applySession = (nextUserId: string | null, event: AuthChangeEvent | "GET_SESSION") => {
      if (isUnmounted) return;

      if (event === "SIGNED_OUT") {
        clearActorRoleHint();
      }

      if (userIdRef.current !== nextUserId) {
        userIdRef.current = nextUserId;
        queryClient.removeQueries({
          predicate: (query) => !shouldKeepQueryOnUserChange(query, nextUserId),
        });
        setSessionUserId(nextUserId);
        return;
      }

      if (!nextUserId) return;

      if (event === "SIGNED_IN") {
        // Same user signing in again (or supabase re-announcing the session): keep a good
        // context, but never let a non-ready result outlive the sign-in.
        void resetNonReadyOwnerContext(queryClient);
      } else if (event === "USER_UPDATED") {
        // Same identity, possibly new profile fields: refresh in the background, keep what's shown.
        void queryClient.invalidateQueries({ queryKey: OWNER_CONTEXT_QUERY_KEY });
      }
    };

    void supabase.auth.getSession().then(({ data }) => {
      applySession(data.session?.user?.id ?? null, "GET_SESSION");
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      applySession(nextSession?.user?.id ?? null, event);
    });

    return () => {
      isUnmounted = true;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const base = useQuery({
    queryKey: ownerContextQueryKey(sessionUserId, null),
    queryFn: () => fetchOwnerContext(null),
    staleTime: OWNER_CONTEXT_STALE_MS,
    gcTime: OWNER_CONTEXT_GC_MS,
    refetchOnWindowFocus: false,
  });

  const baseData = base.data;
  const instructorActor =
    baseData?.status === "ready" && baseData.actorRole === "instructor"
      ? { actorUserId: baseData.actorUserId, actorRole: baseData.actorRole }
      : null;
  const isClientView = Boolean(clientUserId && instructorActor);

  // With no client open (or no resolved instructor) this observes the same entry as `base` and
  // never fetches on its own.
  const client = useQuery({
    queryKey: ownerContextQueryKey(sessionUserId, isClientView ? clientUserId : null),
    // Must match `base`'s queryFn whenever the keys coincide: the last observer to set options
    // decides what a shared entry fetches.
    queryFn: isClientView
      ? () => fetchOwnerContext(clientUserId, instructorActor)
      : () => fetchOwnerContext(null),
    enabled: isClientView,
    staleTime: OWNER_CONTEXT_STALE_MS,
    gcTime: OWNER_CONTEXT_GC_MS,
    refetchOnWindowFocus: false,
  });

  // Until the base context says "instructor", pages see the base context: signed-out /
  // needs-onboarding still propagate on a client URL, and a tracking account never sees the
  // client layer at all (the shell sends it home).
  const current = isClientView ? client : base;

  // Query results are fresh objects every render; only hand consumers a new value when something
  // they read actually changed, so provider re-renders don't re-render the whole app.
  const value = useMemo<OwnerContextState>(
    () => ({ sessionUserId, base, current }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the fields consumers read
    [
      sessionUserId,
      base.data,
      base.status,
      base.fetchStatus,
      base.error,
      current.data,
      current.status,
      current.fetchStatus,
      current.error,
      isClientView,
    ]
  );

  return (
    <OwnerContextStateContext.Provider value={value}>{children}</OwnerContextStateContext.Provider>
  );
};
